import { createContext, useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient, QueryKey, QueryStatus } from '@tanstack/react-query'
import { loginWithEmailAndPassword as loginFn, logout as logoutFn, register as registerFn, getUserIdentity } from 'lib/auth'
import { User } from 'features/auth/types'
import { AxiosError } from 'axios'
import { ValidationErrors } from 'utils/enum'
import { useNavigate, useLocation } from 'react-router-dom'
import { routes } from 'configs/constants'
import { AUTH_STATUS } from './enums'

interface AuthContextValue {
  user: User | null | undefined
  authStatus: QueryStatus
  error: unknown
  refetchUser: () => void
  login: (...arg: any) => void
  isLoggingIn: boolean
  logout: () => void
  isLoggingOut: boolean
  register: (...arg: any) => void
  isRegistering: boolean
  isRegisterError: boolean
  registerError: string
}

export const AuthContext = createContext<AuthContextValue | null>(null)
AuthContext.displayName = 'AuthContext'

export interface AuthProviderProps {
  children: React.ReactNode
  authKey?: QueryKey
  waitInitial?: boolean
}

export function AuthProvider(props: AuthProviderProps) {
  const { children, authKey = ['auth-user'], waitInitial = true } = props
  const queryClient = useQueryClient()

  const [registerApiError, setRegisterApiError] = useState<string>('')

  const { data: user, error, status, fetchStatus, refetch } = useQuery(authKey, getUserIdentity)

  const loginMutation = useMutation(loginFn, {
    onSuccess: user => {
      queryClient.setQueryData(authKey, user)
    },
  })

  const { mutateAsync, isLoading, isError } = useMutation(registerFn, {
    onError: error => {
      if (error instanceof AxiosError) {
        setRegisterApiError(error.response?.data.message)
      } else {
        setRegisterApiError(ValidationErrors.DEFAULT)
      }
    },
  })

  const logoutMutation = useMutation(logoutFn, {
    onSuccess: () => {
      queryClient.clear()
      // setUser(null);
    },
  })

  const navigate = useNavigate()
  const { pathname } = useLocation()

  // redirect to sentinel one key form on first login
  useEffect(() => {
    if (!user) return
    if (user.status && !user.sentinelOneAPiKey && pathname === '/misc/sentinel-one') {
      navigate(routes.sentinel)
    }
  }, [user, navigate, pathname])

  const value = useMemo(
    () => ({
      user,
      authStatus: status,
      error,
      refetchUser: refetch,
      login: loginMutation.mutateAsync,
      isLoggingIn: loginMutation.isLoading,
      logout: logoutMutation.mutateAsync,
      isLoggingOut: logoutMutation.isLoading,
      register: mutateAsync,
      isRegistering: isLoading,
      isRegisterError: isError,
      registerError: registerApiError,
    }),
    [
      user,
      status,
      error,
      refetch,
      loginMutation.mutateAsync,
      loginMutation.isLoading,
      logoutMutation.mutateAsync,
      logoutMutation.isLoading,
      mutateAsync,
      isLoading,
      isError,
      registerApiError,
    ]
  )

  if (status === AUTH_STATUS.SUCESS || !waitInitial) {
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  }

  if (status === AUTH_STATUS.LOADING || fetchStatus === AUTH_STATUS.IDLE) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Something went wrong</div>
  }

  return <div>Unhandled status: {status}</div>
}
